import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation } from "convex/react";
import { LogOut, Upload, UserCircle } from "lucide-react";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "../../convex/_generated/api";
import { Button, Field, Input, Modal, Notice } from "./ui";
import { WlsLogo } from "./WlsLogo";
import { AlertSoundSettings } from "./AlertSoundSettings";
import { errorMessage, ROLE_LABELS } from "../lib/format";

type Account = {
  name?: string;
  discordId?: string;
  role: "admin" | "viewer" | "visitor";
  avatarUrl?: string | null;
};

/**
 * Réglages personnels accessibles à tous les membres. Ils vivent ici plutôt que dans les
 * paramètres d'administration, réservés au propriétaire : chacun doit pouvoir changer son
 * nom et son logo, qui l'identifient dans les logs.
 */
function AccountModal({ me, onClose }: { me: Account; onClose: () => void }) {
  const generateUploadUrl = useMutation(api.users.generateAvatarUploadUrl);
  const setMyAvatar = useMutation(api.users.setMyAvatar);
  const updateMyName = useMutation(api.users.updateMyName);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"avatar" | "name" | null>(null);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");

  const uploadAvatar = async (file: File) => {
    if (file.size > 2_000_000) { setError("Le logo ne doit pas dépasser 2 Mo."); return; }
    setBusy("avatar"); setError(""); setSaved("");
    try {
      const url = await generateUploadUrl();
      const response = await fetch(url, { method: "POST", headers: { "Content-Type": file.type }, body: file });
      if (!response.ok) throw new Error("upload");
      const { storageId } = await response.json();
      await setMyAvatar({ storageId });
      setSaved("Logo mis à jour.");
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setBusy(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveName = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name") ?? "");
    setBusy("name"); setError(""); setSaved("");
    try { await updateMyName({ name }); setSaved("Nom mis à jour."); }
    catch (reason) { setError(errorMessage(reason)); }
    finally { setBusy(null); }
  };

  return (
    <Modal title="Mon compte" description="Votre nom et votre logo apparaissent à côté de chacune de vos actions." onClose={onClose}>
      <div className="avatar-row">
        <div className="avatar-preview">
          {me.avatarUrl ? <img src={me.avatarUrl} alt="" /> : <WlsLogo size={30} />}
        </div>
        <div className="button-row">
          <Button variant="secondary" loading={busy === "avatar"} onClick={() => fileRef.current?.click()}>
            <Upload size={15} /> {me.avatarUrl ? "Changer le logo" : "Ajouter un logo"}
          </Button>
          {me.avatarUrl && <Button variant="ghost" onClick={() => void setMyAvatar({ storageId: undefined })}>Retirer</Button>}
          <input
            ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
            onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadAvatar(file); }}
          />
        </div>
      </div>
      <form onSubmit={saveName} className="form-stack">
        <div className="form-grid">
          <Field label="Nom affiché"><Input name="name" defaultValue={me.name ?? ""} required minLength={2} /></Field>
          <Field label="ID Discord"><Input value={me.discordId ?? "—"} disabled /></Field>
        </div>
        {me.role !== "visitor" && (
          <section className="account-section">
            <h3>Son des alertes de surveillance</h3>
            <AlertSoundSettings />
          </section>
        )}
        {saved && <Notice tone="success">{saved}</Notice>}
        {error && <Notice tone="error">{error}</Notice>}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Fermer</Button>
          <Button type="submit" loading={busy === "name"}>Enregistrer</Button>
        </div>
      </form>
    </Modal>
  );
}

export function AccountMenu({ me }: { me: Account }) {
  const { signOut } = useAuthActions();
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Un menu ouvert doit se refermer au clic ailleurs et à Échap, sinon il reste collé à l'écran.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="account" ref={container}>
      <div className="account__copy">
        <strong>{me.name || "Compte WLS"}</strong>
        <span>{ROLE_LABELS[me.role] ?? me.role}</span>
      </div>
      <button
        type="button"
        className="account__trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu du compte"
      >
        {me.avatarUrl ? <img src={me.avatarUrl} alt="" /> : <WlsLogo size={24} />}
      </button>
      {open && (
        <div className="account__menu" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); setAccountOpen(true); }}>
            <UserCircle size={16} aria-hidden="true" /> Mon compte
          </button>
          <button type="button" role="menuitem" className="account__menu-danger" onClick={() => void signOut()}>
            <LogOut size={16} aria-hidden="true" /> Se déconnecter
          </button>
        </div>
      )}
      {accountOpen && <AccountModal me={me} onClose={() => setAccountOpen(false)} />}
    </div>
  );
}
